import React from 'react';
import styles from '../ReportCard.module.scss';

/**
 * Аналитический блок: статус водителей (круговая диаграмма), заблокированные, статистика по базам.
 */
const AnalyticsSection = ({ statsLoading, statsData, blockedDrivers, blockedDriversLoading, blockedDriversError }) => {
  return (
    <div className={styles.analyticsSection}>
      <div className={styles.analyticsHeader}>
        <h2>Аналитика и статистика перевозок</h2>
      </div>

      <div className={styles.analyticsGrid}>
        {/* Статус водителей */}
        <div className={styles.analyticsCard}>
          <div className={styles.cardHeader}>
            <h3>Статус водителей</h3>
          </div>
          <div className={styles.chartContainer}>
            {statsLoading ? (
              <div className={styles.noData}>Загрузка статистики...</div>
            ) : (
              <DriverStatusChart statsData={statsData} />
            )}
          </div>
        </div>

        {/* Заблокированные водители */}
        <div className={styles.analyticsCard}>
          <div className={styles.cardHeader}>
            <h3>Заблокированный транспорт</h3>
          </div>
          <div className={styles.blockedDriversTable}>
            {blockedDriversLoading && <div>Загрузка...</div>}
            {blockedDriversError && <div style={{ color: 'red' }}>{blockedDriversError}</div>}
            {!blockedDriversLoading && !blockedDriversError && (
              <BlockedDriversTable drivers={blockedDrivers} />
            )}
          </div>
        </div>

        {/* Статистика по базам */}
        <div className={styles.analyticsCard}>
          <div className={styles.cardHeader}>
            <h3>Статистика</h3>
          </div>
          <div className={styles.chartContainer}>
            {statsLoading ? (
              <div className={styles.noData}>Загрузка статистики...</div>
            ) : (
              <BaseStatsChart statsData={statsData} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/** Круговая диаграмма: В погрузке / В пути / Завершили */
const DriverStatusChart = ({ statsData }) => {
  if (!statsData) return <div className={styles.noData}>Нет данных</div>;

  const inLoading = statsData.in_loading_count || 0;
  const inRoute = statsData.in_route_count || 0;
  const completed = statsData.completed_count || 0;
  const total = inLoading + inRoute + completed;
  if (total === 0) return <div className={styles.noData}>Нет данных</div>;

  const inLoadingPercent = (inLoading / total) * 100;
  const inRoutePercent = (inRoute / total) * 100;
  const completedPercent = (completed / total) * 100;

  // Вычисляем длину дуг для SVG (окружность радиусом 80 = 2πr ≈ 502.6)
  const inLoadingArc = inLoadingPercent * 5.026;
  const inRouteArc = inRoutePercent * 5.026;
  const completedArc = completedPercent * 5.026;

  return (
    <>
      <svg viewBox="0 0 200 200" className={styles.pieChart}>
        {/* В погрузке - оранжевый */}
        <circle cx="100" cy="100" r="80" fill="none" stroke="#FF9800" strokeWidth="40"
          strokeDasharray={`${inLoadingArc} 502.6`} transform="rotate(-90 100 100)" />
        {/* В пути - синий */}
        <circle cx="100" cy="100" r="80" fill="none" stroke="#0d6efd" strokeWidth="40"
          strokeDasharray={`${inRouteArc} 502.6`} strokeDashoffset={-inLoadingArc}
          transform="rotate(-90 100 100)" />
        {/* Завершено - зеленый */}
        <circle cx="100" cy="100" r="80" fill="none" stroke="#28a745" strokeWidth="40"
          strokeDasharray={`${completedArc} 502.6`} strokeDashoffset={-(inLoadingArc + inRouteArc)}
          transform="rotate(-90 100 100)" />
      </svg>
      <div className={styles.chartLegend}>
        <div className={styles.legendItem}>
          <span className={styles.legendColor} style={{ background: '#FF9800' }}></span>
          <span className={styles.legendLabel}>В погрузке</span>
          <span className={styles.legendValue}>{inLoading}</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendColor} style={{ background: '#0d6efd' }}></span>
          <span className={styles.legendLabel}>В пути</span>
          <span className={styles.legendValue}>{inRoute}</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendColor} style={{ background: '#28a745' }}></span>
          <span className={styles.legendLabel}>Завершили</span>
          <span className={styles.legendValue}>{completed}</span>
        </div>
      </div>
    </>
  );
};

/** Таблица заблокированных водителей */
const BlockedDriversTable = ({ drivers }) => {
  if (!drivers || drivers.length === 0) {
    return <div className={styles.noData}>Нет заблокированных водителей</div>;
  }
  return (
    <table className={styles.compactTable}>
      <thead>
        <tr>
          <th>№</th>
          <th>ФИО</th>
          <th>Номер машины</th>
          <th>Модель машины</th>
          <th>Дата блокировки</th>
        </tr>
      </thead>
      <tbody>
        {drivers.slice(0, 5).map((driver, idx) => (
          <tr key={idx}>
            <td>{idx + 1}</td>
            <td>{driver.employee_name || '-'}</td>
            <td>{driver.vehicle_number || '-'}</td>
            <td>{driver.vehicle_model || '-'}</td>
            <td>{driver.blocked_date || driver.time_b || driver.time_a || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/** Круговая диаграмма + список по базам: В погрузке / Погружено / Отгружено */
const BaseStatsChart = ({ statsData }) => {
  if (!statsData || !statsData.base_stats) return <div className={styles.noData}>Нет данных</div>;

  const baseStats = statsData.base_stats;
  const totalInLoading = Object.values(baseStats).reduce((sum, b) => sum + (b.in_loading || 0), 0);
  const totalLoaded = Object.values(baseStats).reduce((sum, b) => sum + (b.loaded || 0), 0);
  const totalDelivered = Object.values(baseStats).reduce((sum, b) => sum + (b.delivered || 0), 0);
  const total = totalInLoading + totalLoaded + totalDelivered;
  if (total === 0) return <div className={styles.noData}>Нет данных</div>;

  const inLoadingPercent = (totalInLoading / total) * 100;
  const loadedPercent = (totalLoaded / total) * 100;
  const deliveredPercent = (totalDelivered / total) * 100;

  // Вычисляем длину дуг для SVG
  const inLoadingArc = inLoadingPercent * 5.026;
  const loadedArc = loadedPercent * 5.026;
  const deliveredArc = deliveredPercent * 5.026;

  return (
    <>
      <svg viewBox="0 0 200 200" className={styles.pieChart}>
        {/* В погрузке - оранжевый */}
        <circle cx="100" cy="100" r="80" fill="none" stroke="#FF9800" strokeWidth="40"
          strokeDasharray={`${inLoadingArc} 502.6`} transform="rotate(-90 100 100)" />
        {/* Погружено (в пути) - желтый */}
        <circle cx="100" cy="100" r="80" fill="none" stroke="#ffc107" strokeWidth="40"
          strokeDasharray={`${loadedArc} 502.6`} strokeDashoffset={-inLoadingArc}
          transform="rotate(-90 100 100)" />
        {/* Отгружено (завершено) - голубой */}
        <circle cx="100" cy="100" r="80" fill="none" stroke="#17a2b8" strokeWidth="40"
          strokeDasharray={`${deliveredArc} 502.6`} strokeDashoffset={-(inLoadingArc + loadedArc)}
          transform="rotate(-90 100 100)" />
      </svg>
      <div className={styles.chartLegend}>
        <div className={styles.legendItem}>
          <span className={styles.legendColor} style={{ background: '#FF9800' }}></span>
          <span className={styles.legendLabel}>
            В погрузке <span style={{ fontSize: '0.85em', opacity: 0.8, fontWeight: 500 }}>(Кара-Кече)</span>
          </span>
          <span className={styles.legendValue}>{totalInLoading}</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendColor} style={{ background: '#ffc107' }}></span>
          <span className={styles.legendLabel}>
            Погружено <span style={{ fontSize: '0.85em', opacity: 0.8, fontWeight: 500 }}>(В пути на базу)</span>
          </span>
          <span className={styles.legendValue}>{totalLoaded}</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendColor} style={{ background: '#17a2b8' }}></span>
          <span className={styles.legendLabel}>
            Отгружено <span style={{ fontSize: '0.85em', opacity: 0.8, fontWeight: 500 }}>(Завершено)</span>
          </span>
          <span className={styles.legendValue}>{totalDelivered}</span>
        </div>
      </div>
      <div className={styles.basesList}>
        {Object.entries(baseStats).map(([name, stats]) => (
          <div key={name} className={styles.baseItem}>
            <div className={styles.baseName}>{name}</div>
            <div className={styles.baseStats}>
              <span className={styles.statBadge} style={{ background: '#FF9800', color: '#fff' }}>{stats.in_loading || 0} погр-ся</span>
              <span className={styles.statBadge} style={{ background: '#ffc107', color: '#000' }}>{stats.loaded || 0} погр.</span>
              <span className={styles.statBadge} style={{ background: '#17a2b8', color: '#fff' }}>{stats.delivered || 0} отгр.</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default React.memo(AnalyticsSection);
